import { useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { getDisplayedEntityIds } from "@common/types/domains/accounting/chartOfAccountsViewFilters";
import { FiltersTypes } from "@common/types/filters/general";
import { Button } from "@design-system/Button/Button";
import { Drawer } from "@design-system/Drawer";
import { Loader } from "@design-system/Loader/Loader";
import { EntityFilterCombobox } from "@components/entity/EntityFilter/EntityFilterCombobox";
import {
    accountingReportsQueryKeys,
    useChartOfAccountsQuery,
} from "@api/accountingReports.api";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import { useAccountingTabContext } from "@components/accounting/useAccountingTab";
import { useEntitiesWithAccountingAvailable } from "@components/accounting/useEntitiesWithAccountingAvailable";
import { WorkspaceChartEditorContent } from "./WorkspaceChartEditorContent";
import { useWorkspaceChartEditorExecutor } from "./useWorkspaceChartEditorExecutor";
import { EditorFocusTarget } from "../editorBodyTypes";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    // The row a chart-view Edit came from; null for the Edit COA toolbar button.
    focusTarget?: EditorFocusTarget | null;
    // So a reseed remount does not re-focus.
    onFocusTargetConsumed?: () => void;
}

// Shares the chart page's business filter (same AccountingTabContext), so the
// drawer's picker also moves the page. Every gesture lands on its own; there is
// no save button and no close confirmation, because nothing is ever pending.
// The queue lives here rather than in the content: closing unmounts only the
// content, so a content-owned queue would die with the write it must outlive.
export const WorkspaceChartEditorDrawer = ({
    isOpen,
    onClose,
    focusTarget,
    onFocusTargetConsumed,
}: Props) => {
    const entities = useEntitiesWithAccountingAvailable();
    const { filters, setFilters } = useAccountingTabContext();
    const { data, refetch } = useChartOfAccountsQuery();
    const workspace = useActiveWorkspace();
    const queryClient = useQueryClient();

    // undefined means every business, as on the page tree. Never `?? []` (that
    // is an empty tree).
    const displayedEntityIds = useMemo(
        () =>
            getDisplayedEntityIds(filters) ??
            entities.map((entity) => entity.id),
        [filters, entities],
    );

    // Subtype is dropped: DraftAccount has none. Each open remounts, so this
    // re-reads the chart's live status/source.
    const initialFilters = useMemo<FiltersTypes>(
        () => ({
            accountStatus: filters.accountStatus,
            accountSource: filters.accountSource,
            accountRolePresence: filters.accountRolePresence,
            accountRulePresence: filters.accountRulePresence,
            coaTemplateGroupKeys: filters.coaTemplateGroupKeys,
        }),
        [
            filters.accountStatus,
            filters.accountSource,
            filters.accountRolePresence,
            filters.accountRulePresence,
            filters.coaTemplateGroupKeys,
        ],
    );

    const handleSearchFilterChange = useCallback(
        (search: string | undefined) =>
            setFilters((current) => ({ ...current, search })),
        [setFilters],
    );
    const handleAccountFiltersChange = useCallback(
        (
            next: Pick<
                FiltersTypes,
                | "accountStatus"
                | "accountSource"
                | "accountRolePresence"
                | "accountRulePresence"
                | "coaTemplateGroupKeys"
            >,
        ) =>
            setFilters((current) => ({
                ...current,
                accountStatus: next.accountStatus,
                accountSource: next.accountSource,
                accountRolePresence: next.accountRolePresence,
                accountRulePresence: next.accountRulePresence,
                coaTemplateGroupKeys: next.coaTemplateGroupKeys,
            })),
        [setFilters],
    );

    const refetchChart = useCallback(async () => {
        const result = await refetch();
        if (result.isError || result.data === undefined) {
            throw result.error ?? new Error("Chart of accounts refetch failed");
        }
    }, [refetch]);

    const invalidateChart = useCallback(
        () =>
            queryClient.invalidateQueries({
                queryKey: [accountingReportsQueryKeys.chartOfAccounts],
            }),
        [queryClient],
    );

    const executor = useWorkspaceChartEditorExecutor({
        isOpen,
        workspaceId: workspace?.id ?? null,
        refetchChart,
        invalidateChart,
    });

    const setEntityIds = useCallback(
        (entityIds: number[] | undefined) =>
            setFilters((currentFilters) => ({
                ...currentFilters,
                entityIds: entityIds?.length
                    ? entityIds
                    : entities.map((entity) => entity.id),
            })),
        [setFilters, entities],
    );
    const resetEntityFilter = useCallback(
        () => setEntityIds(undefined),
        [setEntityIds],
    );

    const entityFilter = (
        <EntityFilterCombobox
            filters={{ entityIds: displayedEntityIds }}
            onlyBusiness
            onChange={(newFilters) => setEntityIds(newFilters.entityIds)}
            excludeAllMockEntities
        />
    );

    const retryAction = executor.isRecoveryPending ? (
        <Button variant="primary" size="sm" onClick={executor.retryRecovery}>
            Retry
        </Button>
    ) : undefined;

    const renderBody = () => {
        if (!isOpen) {
            return null;
        }
        // `keepPreviousData` means the query is never empty once loaded, so a
        // reopen waiting on the previous write or cache refresh has to force
        // the loader itself rather than remount onto a cache that predates it.
        if (
            !data ||
            executor.isAwaitingPreviousSession ||
            executor.isResyncing
        ) {
            return <Loader />;
        }
        return (
            <WorkspaceChartEditorContent
                key={executor.contentKey}
                data={data}
                displayedEntityIds={displayedEntityIds}
                entityFilter={entityFilter}
                enqueue={executor.enqueue}
                isEditingDisabled={executor.isRecoveryPending}
                disabledActionAddon={retryAction}
                isWriting={executor.isWriting}
                whenIdle={executor.whenIdle}
                getSessionToken={executor.getSessionToken}
                isSessionCurrent={executor.isSessionCurrent}
                isSessionBlocked={executor.isSessionBlocked}
                trackExternalWrite={executor.trackExternalWrite}
                resync={executor.resync}
                focusTarget={focusTarget}
                onFocusTargetConsumed={onFocusTargetConsumed}
                initialSearchFilter={filters.search}
                initialFilters={initialFilters}
                onSearchFilterChange={handleSearchFilterChange}
                onAccountFiltersChange={handleAccountFiltersChange}
                onEntityFilterReset={resetEntityFilter}
            />
        );
    };

    return (
        <Drawer
            open={isOpen}
            onClose={onClose}
            direction="right"
            size="fullScreen"
            noPadding
            headerAddonLeft={<span>Edit chart of accounts</span>}
        >
            {renderBody()}
        </Drawer>
    );
};
